// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';
import { compose } from 'redux';

import { needsAuth } from '@gandi/authentication';
import {
  NewPaymentForm,
  selectIsFetchingOneOrder,
  selectCurrency,
  selectOrder,
} from '@gandi/billing';
import {
  selectCountItems,
  selectProducts,
  selectDiscounts,
  getCart,
  refreshCart,
} from '@gandi/cart';
import { selectCurrentId } from '@gandi/redux-sharing';
import { sendCommerce } from '@gandi/tracking';
import { localeSelector } from '@gandi/translate';
import { Spinner } from '@gandi/ui';

import { withLogger, type GandiLogger } from 'gandi.redux-logger';

import { useCartSummaryContext } from 'src/Layout/context/CartSummary';
import { withRouter } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectIsAppWorking } from 'src/modules/app';
import { selectShopContextURL } from 'src/modules/config';
import { webhostingSizes } from 'src/modules/simplehosting/prices';
import type { FullState, PreFetchArgs, WithRouterProps } from 'src/types';

import OrderError from './components/OrderError';
import { PreviousPaymentAttemptMessage } from './components/PreviousPaymentAttemptMessage';
import {
  fetchOrderThunk,
  fetchOrderIfNeeded,
  selectOrderError,
  setTemporaryCartTracking,
} from './modules';

type Params = {
  cartId: string,
  orderId: string,
};

type InjectedProps = {
  /* eslint-disable react/no-unused-prop-types */
  // Use in handleredirect
  logger: GandiLogger,
  /* eslint-enable react/no-unused-prop-types */
  ...WithRouterProps<Params>,
};

type Props = {
  ...InjectedProps,
};

function Checkout({
  params,
  params: { cartId },
}: Props): React.MixedElement | null {
  const { flushPrepaid, setFlushPrepaid } = useCartSummaryContext();

  const redirectTo = useAppSelector(state =>
    selectShopContextURL(state, 'payment-return'),
  );

  const orderId = parseInt(params.orderId, 10);
  const order = useAppSelector(state => selectOrder(state, { orderId }));
  const orderError = useAppSelector(state =>
    selectOrderError(state, { orderId }),
  );

  const isAppWorking = useAppSelector(selectIsAppWorking);
  const isFetchingOrder = useAppSelector(state =>
    selectIsFetchingOneOrder(state, { orderId }),
  );

  const loading = isAppWorking || isFetchingOrder;

  const dispatch = useDispatch();
  const [ready, setReady] = React.useState(!orderError);

  const discountsArray = useAppSelector(selectDiscounts);

  const sharingId = useAppSelector(selectCurrentId);
  const cartProducts = useAppSelector(selectProducts);
  const currency = useAppSelector(state =>
    selectCurrency(state, { sharingId }),
  );
  const distinctProducts = useAppSelector(selectCountItems);

  React.useEffect(() => {
    dispatch(fetchOrderIfNeeded(orderId)).then(
      () => setReady(true),
      () => setReady(true),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (order && distinctProducts) {
      sendCommerce({
        name: 'cart.payment',
        data: {
          cart_id: cartId,
          cart_currency: order?.currency,
          cart_turnovertaxincluded: order?.price_after_taxes,
          cart_turnovertaxfree: order?.price_before_taxes,
          cart_quantity: order?.items.length,
          cart_nbdistinctproduct: distinctProducts,
          shipping_delivery: 'none',
          shipping_costtaxincluded: 0,
          shipping_costtaxfree: 0,
        },
      });
    }
    if (cartId) {
      setTemporaryCartTracking({
        cartId,
        // $FlowFixMe[incompatible-call]
        discounts: discountsArray.map(d => d?.code),
        distinctProducts,
        cartProducts,
        // $FlowFixMe[incompatible-call]
        currency,
      });
    }
  }, [
    order,
    cartId,
    dispatch,
    cartProducts,
    discountsArray,
    distinctProducts,
    currency,
  ]);

  if (loading) {
    return <Spinner centered />;
  }

  if (orderError) {
    return <OrderError orderId={orderId} />;
  }

  if (!ready && !loading) {
    return null;
  }

  const context =
    order &&
    order.items.find(
      item =>
        item.product_type === 'paas' &&
        webhostingSizes.includes(item.details?.size) &&
        item.duration === 1 &&
        item.duration_unit === 'm' &&
        item.price_before_taxes > 0,
    )
      ? 'recurrent'
      : 'pay';

  const lockSubmit = loading || orderError;
  return (
    <>
      <PreviousPaymentAttemptMessage />
      <NewPaymentForm
        disableSubmit={lockSubmit}
        order={order}
        context={context}
        redirectTo={redirectTo}
        paymentMethodSave={context === 'recurrent' ? 'mandatory' : 'enabled'}
        flushPrepaid={flushPrepaid}
        setFlushPrepaid={setFlushPrepaid}
      />
    </>
  );
}

Checkout.handleRedirect = async function handleRedirect({
  dispatch,
  getState,
  params: { cartId, orderId },
  logger,
  res,
}: PreFetchArgs<Props, Params, empty, FullState>): Promise<void> {
  // handleRedirect check if an order is still valid and payable
  let responseCartRefresh;
  try {
    await dispatch(fetchOrderThunk(orderId));
    await dispatch(getCart(cartId));
    responseCartRefresh = await dispatch(refreshCart(cartId));
  } catch (err) {
    logger.error({ err }, 'Error while checking the validity of the order.');
  }

  // state is up to date after abobe thunk actions
  const state = getState();

  const orderError = selectOrderError(state, {
    orderId: Number(orderId),
  });
  const isOurCart = state.cart.uuid === cartId;
  const cartRefresh = responseCartRefresh?.data;

  const shouldRedirectToAccountChoice =
    orderError || !isOurCart || !cartRefresh || !cartRefresh.success;

  if (shouldRedirectToAccountChoice) {
    const locale = localeSelector(state);
    const currentId = selectCurrentId(state);
    const redirectTo = `/${locale}/${currentId}/cart/${cartId}/overview/${orderId}`;

    res.redirect(global.__DEV__ ? `/shop${redirectTo}` : redirectTo);
    throw new Error(`Redirect to ${redirectTo}`);
  }

  return Promise.resolve();
};

export default (compose(
  withLogger('shop/checkout'),
  needsAuth,
  withRouter,
)(Checkout): React.ComponentType<{}>);
